import { useEffect, useMemo, useState } from "react";
import { Percent, Building2, Loader2, Calculator, ChevronDown, Euro, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";

const fmtEur = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

/**
 * CommissionInfoCard — Provisions-Anteil je bestätigtem Arbeitgeber + ein
 * kleiner Live-Verdienst-Hochrechner („bei X Verkäufen ≈ Y € Provision").
 */
export default function CommissionInfoCard({ dealerId, embedded = false } = {}) {
    const [allItems, setAllItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sales, setSales] = useState(5);
    const [avgComm, setAvgComm] = useState("");
    const [avgEdited, setAvgEdited] = useState(false);
    const [open, setOpen] = useState(false);
    const [summary, setSummary] = useState(null);

    useEffect(() => {
        api.get("/vermittler/commission-info")
            .then((r) => setAllItems(r.data.items || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    // Bereits erwirtschaftete / bestätigte EUR-Provision (je Arbeitgeber gefiltert).
    useEffect(() => {
        api.get("/me/seller-commissions", { params: dealerId ? { dealer_id: dealerId } : {} })
            .then((r) => setSummary(r.data.summary || {}))
            .catch(() => setSummary({}));
    }, [dealerId]);

    // Bei Einbindung im Firma-Bereich auf den jeweiligen Arbeitgeber filtern.
    const items = useMemo(
        () => (dealerId ? allItems.filter((it) => it.dealer_id === dealerId) : allItems),
        [allItems, dealerId],
    );

    // Bester „Im Dienst"-Anteil für die Hochrechnung.
    const bestPct = useMemo(() => {
        const duty = items.filter((it) => it.work_mode === "duty");
        return Math.max(0, ...(duty.length ? duty.map((it) => Number(it.distribution_pct || 0)) : [0]));
    }, [items]);

    // Ø Fahrzeug-Provision aus echten Verkäufen (höchster Wert über Arbeitgeber),
    // sonst Fallback-Schätzwert 250 €.
    const realAvg = useMemo(() => {
        const vals = items.map((it) => Number(it.avg_vehicle_commission || 0)).filter((v) => v > 0);
        return vals.length ? Math.max(...vals) : null;
    }, [items]);

    // Vorbelegen, sobald echter Wert vorliegt (solange der Nutzer nicht selbst editiert hat).
    useEffect(() => {
        if (!avgEdited) setAvgComm(realAvg != null ? realAvg : 250);
    }, [realAvg, avgEdited]);

    const estimate = (Number(sales) || 0) * (Number(avgComm) || 0) * (bestPct / 100);

    if (loading) {
        return embedded
            ? <div className="pt-4 mt-4 border-t border-gray-200"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>
            : <div className="bg-white border border-gray-200 rounded-sm p-4"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>;
    }
    if (items.length === 0) return null; // nur bei bestätigtem Arbeitgeber anzeigen

    return (
        <div className={embedded ? "pt-4 mt-4 border-t border-gray-200" : "bg-white border border-gray-200 rounded-sm p-4"} data-testid="commission-info-card">
            <div className="flex items-center gap-2 mb-2">
                <Percent className="w-4 h-4 text-[#FFCC00] shrink-0" />
                <h3 className="font-display font-bold text-sm">{embedded ? "Mein Provisions-Anteil & Hochrechner" : "Meine Provision (EUR)"}</h3>
            </div>
            <ul className="space-y-2" data-testid="commission-info-list">
                {items.map((it, i) => (
                    <li key={i} className="flex items-center gap-2 border border-gray-200 rounded-sm px-2.5 py-2" data-testid={`commission-info-${i}`}>
                        <Building2 className="w-4 h-4 text-gray-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-bold truncate">{it.dealer_name}</div>
                            <div className="text-[10px] text-gray-500 truncate">{it.activity_label} · {it.work_mode === "duty" ? "Im Dienst (€)" : "Privat (€-Punkte)"}</div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="text-base font-black font-mono-num text-[#111827] leading-none">{Number(it.distribution_pct || 0).toFixed(0)}%</div>
                            <div className="text-[9px] uppercase tracking-wider text-gray-400">dein Anteil</div>
                        </div>
                    </li>
                ))}
            </ul>

            {/* Live-Verdienst-Hochrechner — einklappbar; eingeklappt zeigt der
                Header die bereits erwirtschaftete (links) und bestätigte (rechts)
                EUR-Provision (User-Wunsch). */}
            <div className="mt-3 pt-3 border-t border-gray-100" data-testid="commission-estimator">
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    className="w-full flex items-center gap-2"
                    data-testid="commission-estimator-toggle"
                    aria-expanded={open}
                >
                    <Calculator className="w-3.5 h-3.5 text-[#FFCC00] shrink-0" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 shrink-0">Verdienst-Hochrechner</span>
                    {!open && (
                        <span className="flex items-center gap-3 ml-auto mr-1" data-testid="commission-collapsed-amounts">
                            <span className="text-right">
                                <span className="block text-[8px] uppercase tracking-wider text-gray-400 leading-none">Erwirtschaftet</span>
                                <span className="flex items-center gap-0.5 text-sm font-black font-mono-num text-[#111827] leading-tight" data-testid="commission-earned-collapsed">
                                    <Euro className="w-3 h-3 text-gray-400" />{fmtEur(summary?.earned || 0)}
                                </span>
                            </span>
                            <span className="text-right">
                                <span className="block text-[8px] uppercase tracking-wider text-gray-400 leading-none">Bestätigt</span>
                                <span className="flex items-center gap-0.5 text-sm font-black font-mono-num text-emerald-700 leading-tight" data-testid="commission-confirmed-collapsed">
                                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />{fmtEur(summary?.confirmed || 0)}
                                </span>
                            </span>
                        </span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? "rotate-180" : ""} ${open ? "ml-auto" : ""}`} />
                </button>
                {open && (
                  <>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                        <label className="block">
                            <span className="text-[10px] text-gray-500">Verkäufe / Monat</span>
                            <input
                                type="number" min="0" value={sales}
                                onChange={(e) => setSales(e.target.value)}
                                className="w-full h-8 rounded-sm border border-gray-300 px-2 text-sm font-mono-num mt-0.5"
                                data-testid="estimator-sales"
                            />
                        </label>
                        <label className="block">
                            <span className="text-[10px] text-gray-500">Ø Provision je Fahrzeug (€)</span>
                            <input
                                type="number" min="0" step="10" value={avgComm}
                                onChange={(e) => { setAvgEdited(true); setAvgComm(e.target.value); }}
                                className="w-full h-8 rounded-sm border border-gray-300 px-2 text-sm font-mono-num mt-0.5"
                                data-testid="estimator-avg-commission"
                            />
                        </label>
                    </div>
                    <div className="mt-2 flex items-baseline justify-between bg-[#FFFBEB] border border-[#FDE68A] rounded-sm px-3 py-2">
                        <span className="text-[11px] text-gray-600">bei {Number(sales) || 0} Verkäufen ({bestPct.toFixed(0)}% Anteil)</span>
                        <span className="text-lg font-black font-mono-num text-[#111827]" data-testid="estimator-result">≈ {fmtEur(estimate)}</span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1.5">
                        {realAvg != null
                            ? "Ø-Provision aus real verkauften Fahrzeugen deines Händlers vorbelegt — anpassbar."
                            : "Schätzwert — der echte EUR-Betrag ergibt sich pro Verkauf aus der Fahrzeug-Provision des Händlers."}
                    </p>
                  </>
                )}
            </div>
        </div>
    );
}
